<template>
  <v-container fluid>
    <v-card :disabled="loadingData" outlined>
      <v-data-table
        :footer-props="footerProps"
        :header-props="headerProps"
        :headers="getHeaders"
        :items="orders"
        :loading="loadingData"
        :loading-text="$t('order.ordersLoading')"
        :no-data-text="$t('order.noOrders')"
        :options.sync="options"
        :server-items-length="totalOrders"
        dense
        item-key="id"
        mobile-breakpoint="650"
      >
        <template v-slot:item.source_order_id="{ item }">
          <v-btn :class="[!isMobile ? '' : 'pr-0']" :to="'/orders/' + item.source_order_id" color="primary" text>
            {{ item.source_order_id }}
          </v-btn>
        </template>

        <template v-slot:item.type="{ item }">
          <span v-if="item.type">{{ getTypeTranslation(item.type) }}</span>
          <span v-else>X</span>
        </template>

        <template v-slot:item.customer="{ item }">
          <span>{{ item.customer.first_name }} {{ item.customer.last_name }}</span>
        </template>

        <template v-slot:item.date="{ item }">
          <span>{{ new Date(item.date) | moment('DD/MM/YYYY HH:mm:ss') }}</span>
        </template>

        <template v-slot:item.source_location_id="{ item }">
          <v-btn
            v-if="item.source_location_id"
            :class="[!isMobile ? '' : 'pr-0']"
            :to="{ name: 'locationEdit', params: { id: item.source_location_id } }"
            block
            color="primary"
            text
          >
            {{ item.source_location_id }}
          </v-btn>
          <span v-else>
            <v-btn block disabled text>X</v-btn>
          </span>
        </template>

        <template v-slot:item.pickup_location_id="{ item }">
          <v-btn
            v-if="item.pickup_location_id"
            :class="[!isMobile ? '' : 'pr-0']"
            :to="{ name: 'locationEdit', params: { id: item.pickup_location_id } }"
            block
            color="primary"
            text
          >
            {{ item.pickup_location_id }}
          </v-btn>
          <span v-else>
            <v-btn block disabled text>X</v-btn>
          </span>
        </template>

        <template v-slot:item.total_price="{ item }">
          {{ item.total_price | formatPrice(item.currency) }}
          {{ item.currency ? item.currency.code : item.currency_code }}
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn :class="[!isMobile ? '' : 'pr-0']" color="primary" text @click="viewDetails(item.order_id)">
            <v-icon v-if="!isMobile && $vuetify.breakpoint.width <= 768">mdi-magnify</v-icon>
            <span v-else>{{ $t('order.viewDetails') }}</span>
          </v-btn>
        </template>

        <template v-slot:item.current_status="{ item }">
          <span>{{ getStatusTranslation(item.current_status) }}</span>
        </template>

        <template v-slot:item.order_id="{ item }">
          <p :style="orderIdColumn" class="mb-0">{{ item.order_id }}</p>
        </template>

        <template v-slot:top>
          <v-toolbar flat>
            <v-row v-if="!isMobile" align-self="center">
              <v-col md="6" class="px-1">
                <o-select-locations
                  v-model="selectedLocation"
                  :items="locations"
                  :multiple="false"
                  :placeholder="$t('subOrder.filterByLocation').toString()"
                  :outlined="true"
                  :clearable="true"
                  @input="locationChange"
                />
              </v-col>
              <v-col md="4" align-self="center" class="px-1">
                <date-filter
                  v-model="dates"
                  :clearable="true"
                  :hide-details="true"
                  :loading-data="loadingData"
                  :placeholder="$t('subOrder.filterByDates')"
                  text-field-append-icon="mdi-calendar"
                  @resetDate="resetDate"
                  @validateDate="validateDate"
                />
              </v-col>
              <v-col md="2" align-self="center" class="pl-1">
                <v-badge :content="nbActiveFilters" :value="nbActiveFilters" overlap>
                  <v-btn :disabled="loadingData" color="primary" outlined @click="displayFilters = !displayFilters">
                    <v-icon>mdi-filter-plus-outline</v-icon>
                  </v-btn>
                </v-badge>
              </v-col>
            </v-row>
            <v-spacer></v-spacer>
            <v-badge v-if="isMobile" class="mr-1" :content="nbActiveFilters" :value="nbActiveFilters" overlap>
              <v-btn color="primary" outlined @click="displayFilters = !displayFilters">
                <v-icon>mdi-filter-plus-outline</v-icon>
              </v-btn>
            </v-badge>
            <v-btn
              :disabled="loadingData || totalOrders === 0"
              class="mr-1"
              color="primary"
              outlined
              @click="showChooseFieldToExport = true"
            >
              <v-icon>mdi-file-delimited</v-icon>
            </v-btn>
            <v-btn :disabled="loadingData" color="primary" outlined @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-row v-if="isMobile" color="white" flat>
            <v-col cols="12">
              <o-select-locations
                v-model="selectedLocation"
                :items="locations"
                class="mr-1 ml-1"
                :multiple="false"
                :placeholder="$t('subOrder.filterByLocation').toString()"
                :outlined="true"
                :clearable="true"
                @input="locationChange"
              />
            </v-col>
            <v-col cols="12">
              <date-filter
                v-model="dates"
                :clearable="true"
                :hide-details="true"
                :loading-data="loadingData"
                :placeholder="$t('subOrder.filterByDates')"
                text-field-append-icon="mdi-calendar"
                text-field-class="mr-1 ml-1"
                @resetDate="resetDate"
                @validateDate="validateDate"
              />
            </v-col>
          </v-row>
        </template>
      </v-data-table>
    </v-card>
    <filter-dialog
      :key="formFiltersKey"
      v-model="displayFilters"
      :fields="formFilters"
      :loadingData="loadingData"
      :title="$t('order.filterOrders')"
      @confirm="filter"
    />
    <export-fields-dialog
      v-model="showChooseFieldToExport"
      :fields="csvHeaderExport"
      :itemsFields="csvItemsExport"
      :csvPaymentsFields="csvPaymentsFieldsExport"
      :title="$t('exportCsv.csvName')"
      @exportCSV="exportCSV"
    />
  </v-container>
</template>

<script>
  import { EventBus } from '../../event-bus'
  import exportFieldsDialog from '@/components/SubOrders/exportFieldsDialog.vue'
  import DateFilter from '@/components/DateFilter'
  import { mapState } from 'vuex'
  import notSetOptionMixin from '@/mixins/notSetOption'

  export default {
    name: 'SubOrdersTableAdmin',
    components: {
      exportFieldsDialog,
      DateFilter,
    },
    mixins: [notSetOptionMixin],
    data() {
      return {
        selectedAll: false,
        formFiltersKey: Date.now(),
        showChooseFieldToExport: false,
        orders: [],
        totalOrders: 0,
        loadingData: false,
        locations: [],
        selectedLocation: null,
        options: {
          itemsPerPage: 15,
          sortDesc: [], // fix the default value change in new vuetify version
        },
        menu: false,
        dates: [],
        previousDates: this.dates,
        displayFilters: false,
        filters: [],
        pollJob: undefined,
        displayFieldSettings: null,
        mobileBreakpoint: 650,
        listRequestId: 0,
      }
    },
    props: {
      status: {
        type: Array,
      },
    },
    computed: {
      ...mapState(['roles', 'isOctipasAdmin']),
      isMobile() {
        return this.$vuetify.breakpoint.width < this.mobileBreakpoint
      },
      nbActiveFilters: function () {
        return this.filters.filter((filter) => filter.active).length
      },
      getHeaders() {
        let headers = []
        if (this.$vuetify.breakpoint.width < 769) {
          headers = this.headersMobile
        } else {
          headers = this.headers
        }
        return headers
      },
      csvHeaderExport() {
        return { text: this.$t('exportCsv.fieldsToExport'), arrayValues: this.csvHeaders }
      },
      csvItemsExport() {
        return { text: this.$t('exportCsv.itemFieldsToExport'), arrayValues: this.itemsFields }
      },
      csvPaymentsFieldsExport() {
        return { text: this.$t('exportCsv.csvPaymentsFieldsExport'), arrayValues: this.paymentsFields }
      },
      types() {
        return [
          { text: this.$t('subOrder.shipFromWarehouse'), value: 'ship_from_warehouse' },
          { text: this.$t('subOrder.clickAndCollect'), value: 'click_and_collect' },
          { text: this.$t('subOrder.inStorePickup'), value: 'in_store_pickup' },
          { text: this.$t('subOrder.shipFromStore'), value: 'ship_from_store' },
          { text: this.$t('subOrder.clickAndReserve'), value: 'click_and_reserve' },
          { text: this.$t('subOrder.interStore'), value: 'inter_store' },
          this.notSetOption,
        ]
      },
      csvHeaders() {
        let headers = [
          { text: this.$t('subOrder.numPreparation'), value: 'order_id' },
          { text: this.$t('exportCsv.sourceOrder'), value: 'source_order_id' },
          { text: this.$t('exportCsv.type'), value: 'type' },
          { text: this.$t('exportCsv.date'), value: 'date' },
          { text: this.$t('exportCsv.cancellationDate'), value: 'cancellation_date' },
          { text: this.$t('exportCsv.location'), value: 'source_location_id' },
          { text: this.$t('exportCsv.pickupLocation'), value: 'pickup_location_id' },
          { text: this.$t('exportCsv.originLocation'), value: 'origin_location_id' },
          { text: this.$t('exportCsv.currentStatus'), value: 'current_status' },
          { text: this.$t('exportCsv.products'), value: 'products' },
          { text: this.$t('exportCsv.validatedProduct'), value: 'validated_products' },
          { text: this.$t('exportCsv.totalPrice'), value: 'total_price' },
          { text: this.$t('exportCsv.totalPriceExcludeTax'), value: 'total_price_exclude_tax' },
          { text: this.$t('exportCsv.validatedAmount'), value: 'validated_amount' },
          { text: this.$t('exportCsv.validatedAmountExcludeTax'), value: 'validated_amount_ht' },
          { text: this.$t('exportCsv.currencyCode'), value: 'currency_code' },
          { text: this.$t('exportCsv.cancelReason'), value: 'cancel_reason' },
          { text: this.$t('exportCsv.deliveryCountry'), value: 'delivery_country' },
          { text: this.$t('exportCsv.updatedAt'), value: 'updated_at' },
          { text: this.$t('exportCsv.origin'), value: 'origin' },
          { text: this.$t('exportCsv.subChannel'), value: 'sub_channel' },
          { text: this.$t('exportCsv.putBackInStock'), value: 'put_back_in_stock' },
          { text: this.$t('exportCsv.carrierCost'), value: 'carrier_cost' },
          { text: this.$t('exportCsv.carrierTaxAmount'), value: 'carrier_tax_amount' },
          { text: this.$t('exportCsv.carrierName'), value: 'carrier_name' },
          { text: this.$t('exportCsv.carrierType'), value: 'carrier_type' },
          { text: this.$t('exportCsv.carrierCode'), value: 'carrier_code' },
          { text: this.$t('exportCsv.erpCarrierId'), value: 'erp_carrier_id' },
        ]

        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({ text: this.$t('exportCsv.customer'), value: 'customer' })
        }

        return headers
      },
      itemsFields() {
        return [
          { text: this.$t('exportCsv.productId'), value: 'product_id' },
          { text: this.$t('exportCsv.barcode'), value: 'barcode' },
          { text: this.$t('exportCsv.productSku'), value: 'product_sku' },
          { text: this.$t('exportCsv.productTitle'), value: 'product_title' },
          { text: this.$t('exportCsv.productSalePriceExcludeTax'), value: 'product_sale_price_exclude_tax' },
          { text: this.$t('exportCsv.productSalePrice'), value: 'product_sale_price' },
          { text: this.$t('exportCsv.rowTotalPrice'), value: 'row_total_price' },
          { text: this.$t('exportCsv.productDefaultPrice'), value: 'product_default_price' },
          { text: this.$t('exportCsv.rowTotalPriceExcludeTax'), value: 'row_total_price_exclude_tax' },
          { text: this.$t('exportCsv.currentStatus'), value: 'current_status_line' },
          { text: this.$t('exportCsv.validatedQuantity'), value: 'validated_quantity' },
          { text: this.$t('exportCsv.quantity'), value: 'product_quantity' },
        ]
      },
      headersMobile() {
        let headers = [
          {
            text: this.$t('subOrder.numPreparation'),
            value: 'order_id',
            sortable: false,
            filterType: 'text',
          },
          // {
          //   text: this.$t("order.sourceOrder"),
          //   value: "source_order_id",
          //   sortable: false,
          //   filterType: "text"
          // },
          {
            text: this.$t('order.date'),
            value: 'date',
          },
        ]
        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({
            text: this.$t('order.client'),
            value: 'customer',
            filterType: 'text',
          })
        }

        headers.push(
          {
            text: this.$t('order.amount'),
            value: 'total_price',
            filterType: 'range',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          }
        )
        return headers
      },
      formFilters() {
        let headers = [
          {
            text: this.$t('subOrder.numPreparation'),
            value: 'order_id',
            filterType: 'text',
          },
          {
            text: this.$t('order.sourceOrder'),
            value: 'source_order_id',
            filterType: 'text',
          },
          {
            text: this.$t('order.productId'),
            value: 'product_id',
            filterType: 'text',
          },
          {
            text: this.$t('order.type'),
            value: 'type',
            filterType: 'array',
            arrayValues: this.types,
          },
          {
            text: this.$t('order.origin'),
            value: 'origin',
            filterType: 'array',
            arrayValues: this.origin,
          },
          {
            text: this.$t('order.subChannel'),
            value: 'sub_channel',
            filterType: 'text',
          },
        ]

        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({
            text: this.$t('order.client'),
            value: 'customer',
            filterType: 'text',
          })
        }

        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_email', this.roles[0])
        ) {
          headers.push({
            text: this.$t('subOrder.email'),
            value: 'email',
            filterType: 'text',
          })
        }

        headers.push(
          {
            text: this.$t('order.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.status
              ? this.statusTranslations.filter((status) => this.status.includes(status.value))
              : this.statusTranslations,
          },
          {
            text: this.$t('order.amount'),
            value: 'total_price',
            filterType: 'range',
          },
          {
            text: this.$t('subOrder.sourceLocation'),
            value: 'source_location_id',
            filterType: 'array',
            arrayValues: this.filterLocations,
          },
          {
            text: this.$t('subOrder.cancelReasons'),
            value: 'cancel_reasons',
            filterType: 'array',
            arrayValues: [
              { text: this.$t('subOrder.clientChangeOfMind'), value: 'client_changed_his_mind' },
              { text: this.$t('subOrder.deterioratedProducts'), value: 'deteriorated_products' },
              { text: this.$t('subOrder.orderedMultipleTimes'), value: 'ordered_multiple_times' },
              { text: this.$t('subOrder.productsNotAvailable'), value: 'products_not_available' },
              { text: this.$t('subOrder.timeout'), value: 'timeout' },
            ],
          },
          {
            text: this.$t('subOrder.originLocationId'),
            value: 'origin_location_id',
            filterType: 'array',
            arrayValues: this.filterLocations,
          },
          {
            text: this.$t('subOrder.toPutBackInStock'),
            value: 'put_back_in_stock',
            filterType: 'checkbox',
          }
        )
        return headers
      },
      statusTranslations() {
        return [
          { text: this.$t('order.waiting'), value: 'waiting' },
          { text: this.$t('order.waitingPayment'), value: 'waiting_payment' },
          { text: this.$t('order.confirmed'), value: 'confirmed' },
          { text: this.$t('order.partiallyConfirmed'), value: 'partially_confirmed' },
          { text: this.$t('order.toPickup'), value: 'to_pickup' },
          { text: this.$t('order.waitingCarrier'), value: 'waiting_carrier' },
          { text: this.$t('order.shippingInProgress'), value: 'shipping_in_progress' },
          { text: this.$t('order.partialShippingInProgress'), value: 'partial_shipping_in_progress' },
          { text: this.$t('order.delivered'), value: 'delivered' },
          { text: this.$t('order.deliveredPartially'), value: 'partially_delivered' },
          { text: this.$t('order.cancelled'), value: 'cancelled' },
          { text: this.$t('order.error'), value: 'error' },
          { text: this.$t('subOrder.waitingAllocation'), value: 'waiting_allocation' },
          { text: this.$t('order.blocked'), value: 'blocked' },
        ]
      },
      headers() {
        let headers = [
          {
            text: this.$t('subOrder.numPreparation'),
            value: 'order_id',
            sortable: false,
            filterType: 'text',
          },
          {
            text: this.$t('order.sourceOrder'),
            value: 'source_order_id',
            filterType: 'text',
          },
          {
            text: this.$t('order.type'),
            value: 'type',
            filterType: 'array',
            arrayValues: this.types,
          },
          {
            text: this.$t('order.origin'),
            value: 'origin',
          },
          {
            text: this.$t('order.date'),
            value: 'date',
          },
          {
            text: this.$t('subOrder.sourceLocation'),
            value: 'source_location_id',
            align: 'center',
            sortable: false,
          },
          {
            text: this.$t('subOrder.pickupLocation'),
            value: 'pickup_location_id',
            align: 'center',
            sortable: false,
          },
        ]
        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({
            text: this.$t('order.client'),
            value: 'customer',
            filterType: 'text',
          })
        }

        headers.push(
          {
            text: this.$t('order.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.statusTranslations,
          },
          {
            text: this.$t('order.amount'),
            value: 'total_price',
            filterType: 'range',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          }
          // {
          //     text: "",
          //     value: "returnAction",
          //     sortable: false,//}
        )
        return headers
      },
      filterLocations() {
        let arrayValues = []
        if (this.locations.length) {
          for (let location of this.locations) {
            arrayValues.push({
              text: `${location.location_id} ${location.name}`,
              value: location.location_id,
            })
          }
        }
        return arrayValues
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('order.ordersPerPage'),
        }
      },
      headerProps() {
        return {
          sortByText: this.$t('order.sortByText'),
        }
      },
      origin() {
        return [
          { text: this.$t('order.web'), value: 'web' },
          { text: this.$t('order.shop'), value: 'shop' },
          { text: this.$t('order.marketplace'), value: 'marketplace' },
        ]
      },
      orderIdColumn() {
        if (this.$vuetify.breakpoint.width < 450) {
          return 'max-width: 150px'
        }
        if (this.$vuetify.breakpoint.width < 650) {
          return 'max-width: 250px'
        }
        if (this.$vuetify.breakpoint.width < 768) {
          return 'max-width: 101px'
        }
        if (this.$vuetify.breakpoint.width === 768) {
          return 'max-width: 215px'
        }
        if (this.$vuetify.breakpoint.width < 1264) {
          return 'max-width: 250px'
        } else {
          return 'max-width: 300px'
        }
      },
      paymentsFields() {
        return [
          { text: this.$t('exportCsv.paymentMethodId'), value: 'payment_method_id' },
          { text: this.$t('exportCsv.paymentAmount'), value: 'payment_amount' },
          { text: this.$t('exportCsv.paymentReference'), value: 'payment_reference' },
          { text: this.$t('exportCsv.erpPaymentMethodId'), value: 'erp_payment_method_id' },
          { text: this.$t('exportCsv.capturedAmount'), value: 'captured_amount' },
        ]
      },
    },
    watch: {
      options: {
        handler() {
          this.list()
        },
      },
      menu: {
        handler() {
          if (!this.menu && this.dates.length > 0 && this.dates !== this.previousDates) {
            this.previousDates = this.dates
            this.reloadList()
          }
        },
      },
      dates: {
        handler() {
          if (this.dates == null) {
            this.dates = []
            this.previousDates = this.dates
            this.reloadList()
          }
        },
      },
      // A segment change only swaps the props: keep-alive reuses the instance, activated() won't fire.
      $props: {
        handler() {
          this.orders = []
          this.filters = []
          this.formFiltersKey = Date.now()
          this.options.page = 1
          this.list()
        },
        deep: true,
        immediate: false,
      },
    },
    async mounted() {
      await this.getAllLocations()
      await this.getSettingsByCountry('FRA')
    },
    activated() {
      const splittedPath = this.$store.state.previousPath.split('/')
      const isLocationDetails = splittedPath.length === 3 && splittedPath[1] === 'locations'
      const isOrderDetails = splittedPath.length === 3 && splittedPath[1] === 'orders'
      if (splittedPath[1] !== 'sub-orders' && !isLocationDetails && !isOrderDetails) {
        this.formFiltersKey = Date.now()
        this.filters = []
        this.options.page = 1
        this.options.itemsPerPage = 15
        this.options.sortBy = []
        this.options.sortDesc = []
        this.selectedLocation = null
        this.dates = []
      }
      this.orders = []
      this.list()
    },
    deactivated() {
      clearInterval(this.pollJob)
    },
    methods: {
      buildMyInit: function (pagination = true) {
        let { page, itemsPerPage, sortDesc, sortBy } = this.options
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            sortBy: sortBy.length === 0 ? 'date' : sortBy,
            sortDesc: sortDesc.length === 0 ? true : sortDesc,
          },
        }
        if (pagination) {
          myInit.queryStringParameters.page = page
          myInit.queryStringParameters.itemsPerPage = itemsPerPage
        } else {
          myInit.queryStringParameters.itemsPerPage = false
        }
        if (this.selectedLocation) {
          myInit.queryStringParameters.locationId = this.selectedLocation
        }
        if (this.status) {
          myInit.queryStringParameters.status = this.status
        }

        let filters = {}
        for (let filter of this.filters) {
          if (filter.active) {
            if (filter.name === 'type') {
              myInit.queryStringParameters.types = filter.value
            } else if (filter.name === 'current_status') {
              myInit.queryStringParameters.status = filter.value
            } else if (filter.name === 'origin_location_id') {
              myInit.queryStringParameters.origin_location_id = filter.value
            } else if (filter.name === 'source_location_id') {
              myInit.queryStringParameters.source_location_id = filter.value
            } else if (filter.name === 'origin') {
              myInit.queryStringParameters.origin = filter.value
            } else if (filter.name === 'put_back_in_stock') {
              myInit.queryStringParameters.put_back_in_stock = filter.value
            } else {
              filters[filter.name] = filter.value
            }
          }
        }
        if (this.dates.length) {
          let start = new Date(this.dates[0])
          let end = new Date()
          if (this.dates.length > 1) {
            end = new Date(this.dates[1])
          }
          if (start > end) {
            filters.date = { from: end, to: start }
          } else {
            filters.date = { from: start, to: end }
          }
        }
        if (Object.entries(filters).length !== 0) {
          myInit.queryStringParameters.filters = JSON.stringify(filters)
        }
        return myInit
      },
      list() {
        // Several triggers can overlap (props watcher, activated(), options watcher): only the response
        // to the latest request may update the table, whatever order the responses come back in.
        const requestId = ++this.listRequestId
        this.loadingData = true
        let myInit = this.buildMyInit(true)
        this.$Amplify.API.get('sub-orders', '/v1/int/sub-orders', myInit)
          .then((res) => {
            if (requestId !== this.listRequestId) {
              return
            }
            this.orders = res.data
            this.totalOrders = res.nbSubOrders
            this.loadingData = false
            if (this.totalOrders < (this.options.page - 1) * this.options.itemsPerPage + 1) {
              let pageNumber = Math.floor(this.totalOrders / this.options.itemsPerPage)
              this.options.page = pageNumber === 0 ? 1 : pageNumber
            }
          })
          .catch((e) => {
            if (requestId !== this.listRequestId) {
              return
            }
            this.loadingData = false
            // alert(e)
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('subOrder.ordersLoadingErrorMessage'),
              color: 'error',
            })
          })
      },
      viewDetails(subOrderId) {
        this.$router.push('/sub-orders/' + subOrderId)
      },
      async reloadList() {
        await this.$auth.isAuthenticated()
        EventBus.$emit('reLoadCounter')
        this.list()
      },
      locationChange(selectedLocation) {
        this.selectedLocation = selectedLocation
        this.reloadList()
      },
      filter(filters) {
        this.filters = filters
        this.reloadList()
      },
      resetDate() {
        this.dates = []
        this.previousDates = this.dates
        this.reloadList()
      },
      validateDate() {
        if (this.dates.length && this.dates !== this.previousDates) {
          this.previousDates = this.dates
          this.reloadList()
        }
      },
      getTypeTranslation(typeValue) {
        let type = this.types.find(function (type) {
          if (type.value === typeValue) {
            return type
          }
        })
        if (type) {
          return type.text
        }
      },
      getStatusTranslation(statusValue) {
        let status = this.statusTranslations.find(function (status) {
          if (status.value === statusValue) {
            return status
          }
        })
        if (status) {
          return status.text
        }

        return statusValue
      },
      async getAllLocations() {
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            itemsPerPage: false,
            fields: ['location_id', 'name'],
          },
        }
        let vm = this
        this.$Amplify.API.get('locations', '/v1/int/locations', myInit)
          .then(function (locations) {
            vm.locations = locations.data
            vm.formFiltersKey = Date.now()
          })
          .catch(function (e) {
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: vm.$t('orderWorkflow.locationsLoadingErrorMessage'),
              color: 'error',
            })
          })
      },
      exportCSV(value) {
        EventBus.$emit('displaySnackbar', {
          display: true,
          text: this.$t('order.CSVGenerationInProgress'),
          color: 'success',
        })
        /* get order items without pagination */
        this.loadingData = true
        let myInit = this.buildMyInit(false)

        myInit.queryStringParameters.csvHeaders = JSON.stringify(value.csvHeadersToExport)
        myInit.queryStringParameters.exportItemsLine = value.exportItemsLine
        myInit.queryStringParameters.exportCustomFields = value.exportCustomFields
        myInit.queryStringParameters.exportCustomItemFields = value.exportCustomItemFields
        let vm = this
        this.$Amplify.API.get('orders', '/v1/int/sub-orders/export', myInit)
          .then(function (res) {
            if (res.job_id) {
              vm.pollJob = setInterval(function () {
                vm.$Amplify.API.get('orders', '/v1/int/jobs/' + res.job_id, {
                  headers: { Authorization: 'Bearer ' + vm.$auth.accessToken },
                })
                  .then(function (res) {
                    let url = res.file_archive_url
                    if (url) {
                      //we create an <a> element and we simulate a click on it to trigger the downloading
                      const link = document.createElement('a')
                      link.href = url
                      document.body.appendChild(link)
                      link.click()
                      clearInterval(vm.pollJob)
                      vm.loadingData = false
                    } else if (res.status === 'error') {
                      console.log(res.error_message)
                      vm.loadingData = false
                      clearInterval(vm.pollJob)
                      EventBus.$emit('displaySnackbar', {
                        display: true,
                        text: vm.$t('subOrder.jobLoadingErrorMessage'),
                        color: 'error',
                      })
                    }
                  })
                  .catch(function (e) {
                    console.log(e)
                    clearInterval(this.pollJob)
                    vm.loadingData = false
                    EventBus.$emit('displaySnackbar', {
                      display: true,
                      text: vm.$t('subOrder.jobLoadingErrorMessage'),
                      color: 'error',
                    })
                  })
              }, 500)
            } else {
              vm.loadingData = false
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('subOrder.jobLoadingErrorMessage'),
                color: 'error',
              })
            }
          })
          .catch(function (e) {
            console.log(e)
            vm.loadingData = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: vm.$t('subOrder.jobCreationErrorMessage'),
              color: 'error',
            })
          })
      },
      getSettingsByCountry(countryIso3) {
        // get the settings of the country selected
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }

        this.$Amplify.API.get('settings', '/v1/settings/' + countryIso3, myInit)
          .then((res) => {
            this.displayFieldSettings = res.display?.customer_information
            this.formFiltersKey = Date.now()
          })
          .catch((e) => {
            console.log(e)
          })
      },
    },
  }
</script>
